import React from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Button,
  Spinner,
  Chip,
} from '@material-tailwind/react';
import {
  XMarkIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

const formatPrice = (value, currency = 'USD') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatHours = (value) => {
  if (!value && value !== 0) return 'N/A';
  return value.toLocaleString();
};

function SourceListingsModal({ open, onClose, title, date, listings, isLoading }) {
  return (
    <Dialog open={open} handler={onClose} size="xl">
      <DialogHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TableCellsIcon className="h-6 w-6 text-blue-500" />
          <div>
            <Typography variant="h5">{title || 'Source Listings'}</Typography>
            {date && (
              <Typography variant="small" color="gray">
                {date}
              </Typography>
            )}
          </div>
        </div>
        <Button variant="text" onClick={onClose} className="p-2">
          <XMarkIcon className="h-5 w-5" />
        </Button>
      </DialogHeader>

      <DialogBody divider className="max-h-[60vh] overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="h-8 w-8" />
            <Typography className="ml-3">Loading listings...</Typography>
          </div>
        ) : listings && listings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max table-auto text-left">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-3">
                    <Typography variant="small" className="font-semibold text-gray-700">
                      Registration
                    </Typography>
                  </th>
                  <th className="p-3">
                    <Typography variant="small" className="font-semibold text-gray-700">
                      Year
                    </Typography>
                  </th>
                  <th className="p-3">
                    <Typography variant="small" className="font-semibold text-gray-700">
                      Model
                    </Typography>
                  </th>
                  <th className="p-3">
                    <Typography variant="small" className="font-semibold text-gray-700">
                      Price
                    </Typography>
                  </th>
                  <th className="p-3">
                    <Typography variant="small" className="font-semibold text-gray-700">
                      Hours
                    </Typography>
                  </th>
                  <th className="p-3">
                    <Typography variant="small" className="font-semibold text-gray-700">
                      Source
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing, index) => (
                  <tr key={listing.listingId || index} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <Typography variant="small" className="font-medium">
                        {listing.registration || 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="small">
                        {listing.year || 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="small">
                        {listing.model || 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="small" className="font-medium">
                        {formatPrice(listing.price, listing.currency)}
                      </Typography>
                    </td>
                    <td className="p-3">
                      <Typography variant="small">
                        {formatHours(listing.totalTime)}
                      </Typography>
                    </td>
                    <td className="p-3">
                      {listing.source ? (
                        <Chip size="sm" value={listing.source} variant="ghost" />
                      ) : (
                        <Typography variant="small" color="gray">N/A</Typography>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Typography>No listings available</Typography>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <div className="flex items-center justify-between w-full">
          {listings && listings.length > 0 && (
            <Typography variant="small" color="gray">
              {listings.length} listing{listings.length !== 1 ? 's' : ''}
            </Typography>
          )}
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}

export default SourceListingsModal;
